import type { Maybe } from '@adornis/base/utilTypes.js';
import { alertDialog, confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { format, subSeconds } from 'date-fns';
import { html } from 'lit';
import { AccountingMatchingAPI } from '../../../accounting-matching/api/AccountingMatchingAPI.js';
import { TaxAdvisorImportAPI } from '../../../accounting-import/api/AccountingImportAPI.js';
import { AccountingImportMode } from '../../../accounting-import/entities/AccountingImportMode.js';
import { sessionExtend } from '../../../client/session-timeout.js';
import {
  renderDashboardCardHeader,
  renderDashboardLoadingCard,
  renderReadableInteger,
  renderUptime,
} from './StatRenderUtilities.js';

type LastImportInfo = {
  createdAt?: Maybe<Date>;
  numberOfImportedEntries?: Maybe<number>;
  secondsTaken?: Maybe<number>;
  totalAmount?: Maybe<number>;
};

/** Each level includes the one above it; the texts say what it costs, so the smallest one that helps gets picked. */
const IMPORT_MODES = [
  {
    mode: AccountingImportMode.CHANGED_FILES,
    title: 'Normal',
    description:
      'Importiert nur Dateien, die sich in Sharepoint geändert haben, und berechnet die Zuordnung ihrer Schulen neu – so läuft der Import jede Nacht.',
  },
  {
    mode: AccountingImportMode.RESOLVE_ALL,
    title: 'Zuordnungen neu berechnen',
    description:
      'Zusätzlich werden Personen, Kontoinhaber und Vorgänge aller Schulen neu ermittelt. Sinnvoll nach Änderungen an den Zuweisungsheuristiken oder Tiefgreifenden Änderungen an den Stammdaten, Buchungen und Verknüpfungen der Schulen.',
  },
  {
    mode: AccountingImportMode.FULL,
    title: 'Kompletter Import',
    description:
      'Zusätzlich wird jede Datei erneut heruntergeladen und eingelesen, auch unveränderte. Dauert deutlich länger. Im Portal ergänzte Daten an den Buchungen bleiben erhalten. Übernimmt auch Dateien, die mehr als die Hälfte ihrer Buchungen entfernen würden.',
  },
];

export const AccountingImportSection = stateful(() => {
  const { value: lastImportInfo, loading: lastImportInfoLoading } = useQuery(
    () =>
      TaxAdvisorImportAPI.getLastImportInfo()({
        createdAt: 1,
        numberOfImportedEntries: 1,
        secondsTaken: 1,
        totalAmount: 1,
      }),
    [],
  );
  const importInfo = lastImportInfo as Maybe<LastImportInfo>;
  const { value: fileProblems } = useQuery(
    () => TaxAdvisorImportAPI.getFileProblems()({ path: 1, error: 1, missingSince: 1, modifiedAt: 1 }),
    [],
  );

  if (lastImportInfoLoading) {
    return renderDashboardLoadingCard();
  }

  return html`
    <div class="card h-full">
      <div class="card-body gap-4">
        ${renderDashboardCardHeader('Buchhaltungsimport', 'icon-[material-symbols--receipt-long-outline-rounded]')}

        <!-- below the header, not in it: header actions never wrap, and two long labels overflow a phone -->
        <div class="flex flex-wrap gap-2">
          <button
            class="btn btn-primary btn-outline"
            @click=${async () => {
              // plain radios: the dialog renders once, the browser keeps which one is checked
              let mode = AccountingImportMode.CHANGED_FILES;
              const confirmed = await confirmDialog(
                html`
                  <div class="flex flex-col gap-3">
                    <span class="font-bold">Wie gründlich soll importiert werden?</span>
                    ${IMPORT_MODES.map(
                      option => html`
                        <label class="flex cursor-pointer items-start gap-3 rounded-box border border-base-300 p-3">
                          <input
                            type="radio"
                            name="accounting-import-mode"
                            class="radio radio-primary mt-1"
                            ?checked=${option.mode === mode}
                            @change=${() => (mode = option.mode)}
                          />
                          <span class="flex flex-col">
                            <span class="font-semibold">${option.title}</span>
                            <span class="text-sm opacity-70">${option.description}</span>
                          </span>
                        </label>
                      `,
                    )}
                  </div>
                `,
                { acceptText: 'Import starten', declineText: 'Abbrechen', highlightDecline: true },
              );
              if (!confirmed) return;

              const exSession = setInterval(() => {
                sessionExtend();
              }, 5000);
              // the last thing the run emits is its summary — most runs find no changed file and should say so
              await guardDialog(() => TaxAdvisorImportAPI.startImport(mode)(), {
                success: summary => alertDialog(html`<p>${summary}</p>`),
                errorMessagePrefix: 'Fehler beim Import',
              });
              clearInterval(exSession);
            }}
          >
            <span class="icon-[material-symbols--cloud-download-outline-rounded] size-5"></span>
            <span>Aus Sharepoint importieren</span>
          </button>
          <button
            class="btn btn-outline"
            @click=${async () => {
              const confirmed = await confirmDialog(
                html`<p>
                  Alle Buchungen der Personenkonten werden im Hintergrund neu zu Vorgängen gruppiert. Das dauert einige
                  Minuten; die Buchungen bleiben währenddessen benutzbar.
                </p>`,
                { acceptText: 'Neu gruppieren', declineText: 'Abbrechen', highlightDecline: true },
              );
              if (!confirmed) return;
              await guardDialog(() => AccountingMatchingAPI.regroupAll()(), {
                success: accounts => alertDialog(html`<p>${accounts} Konten zur Gruppierung eingereiht.</p>`),
                errorMessagePrefix: 'Fehler beim Gruppieren',
              });
            }}
          >
            <span class="icon-[material-symbols--account-tree-outline-rounded] size-5"></span>
            <span>Buchungen neu gruppieren</span>
          </button>
        </div>

        <div class="stats stats-vertical w-full sm:stats-horizontal">
          <div class="stat">
            <div class="stat-figure text-base-content/40">
              <span class="icon-[material-symbols--history-outline-rounded] size-8"></span>
            </div>
            <div class="stat-title">Letzter Import</div>
            <div class="stat-value text-2xl">
              ${importInfo?.createdAt ? format(importInfo.createdAt, 'dd.MM. HH:mm') : '–'}
            </div>
          </div>
          <div class="stat">
            <div class="stat-figure text-base-content/40">
              <span class="icon-[material-symbols--note-add-outline-rounded] size-8"></span>
            </div>
            <div class="stat-title">Neue Einträge</div>
            <div class="stat-value text-2xl">${renderReadableInteger(importInfo?.numberOfImportedEntries ?? 0)}</div>
            <div class="stat-desc">von ${renderReadableInteger(importInfo?.totalAmount ?? 0)} Einträgen</div>
          </div>
          <div class="stat">
            <div class="stat-figure text-base-content/40">
              <span class="icon-[material-symbols--timer-outline-rounded] size-8"></span>
            </div>
            <div class="stat-title">Dauer</div>
            <div class="stat-value text-2xl">
              ${renderUptime(
                importInfo?.createdAt ? subSeconds(importInfo.createdAt, importInfo?.secondsTaken ?? 0) : undefined,
                importInfo?.createdAt,
              )}
            </div>
          </div>
        </div>

        ${
          fileProblems?.length
            ? html`
                <div role="alert" class="alert alert-warning items-start">
                  <span class="icon-[material-symbols--folder-off-outline-rounded] size-6"></span>
                  <div class="min-w-0">
                    <p class="font-semibold">${fileProblems.length} Dateien mit Problemen</p>
                    <ul class="mt-1 flex flex-col gap-1 text-sm">
                      ${fileProblems.map(
                        file =>
                          html`<li class="break-all">
                            <span class="font-semibold">${file.path}</span>: ${file.error || 'fehlt in Sharepoint'}
                            ${file.modifiedAt ? `(Stand der Datei ${format(file.modifiedAt, 'dd.MM.yyyy HH:mm')})` : ''}
                          </li>`,
                      )}
                    </ul>
                  </div>
                </div>
              `
            : html`
                <p class="flex items-center gap-2 text-sm text-base-content/70">
                  <span class="icon-[material-symbols--check-circle-outline-rounded] size-5 text-success"></span>
                  Alle Dateien importiert
                </p>
              `
        }
      </div>
    </div>
  `;
});
